import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, UserPlus, X } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", tier: "basic", upline_user_id: "", zips: [], internal_note: "" };

function UplinePicker({ reps, value, onChange }) {
  const [q, setQ] = useState("");
  const sel = reps.find((r) => r.user_id === value);
  const list = reps.filter((r) => `${r.name} ${r.email} ${r.referly_code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  return (
    <div>
      <Label className="text-xs">Upline / recruited by</Label>
      <div className="mb-1 text-sm text-sam-navy" data-testid="dh-upline-selected">{sel ? <>{sel.name} <span className="text-xs text-sam-slate">({sel.referly_code})</span>{!sel.has_recruit_link && <span className="ml-1 text-xs text-sam-crimson">no recruitment link saved</span>}</> : "None – reports to Saving America Money"}</div>
      <Input placeholder="Search reps by name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dh-upline-search" />
      {q && (
        <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-white">
          <button type="button" className="block w-full px-2 py-1.5 text-left text-sm hover:bg-slate-50" onClick={() => { onChange(""); setQ(""); }} data-testid="dh-upline-none">None – reports to Saving America Money</button>
          {list.map((r) => <button type="button" key={r.user_id} className="block w-full px-2 py-1.5 text-left text-sm hover:bg-slate-50" onClick={() => { onChange(r.user_id); setQ(""); }} data-testid={`dh-upline-opt-${r.user_id}`}>{r.name} <span className="text-xs text-sam-slate">{r.email}</span></button>)}
        </div>
      )}
    </div>
  );
}

function ZipPicker({ zips, value, onChange }) {
  return (
    <div>
      <Label className="text-xs">Prospector ZIPs (optional)</Label>
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="dh-zips">
        {zips.map((z) => {
          const on = value.includes(z);
          return <button type="button" key={z} onClick={() => onChange(on ? value.filter((x) => x !== z) : [...value, z])} data-testid={`dh-zip-${z}`}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${on ? "border-sam-navy bg-sam-navy text-white" : "border-border text-sam-navy"}`}>{z}{on && <X className="ml-1 inline h-3 w-3" />}</button>;
        })}
        {!zips.length && <span className="text-xs text-sam-slate">No routes or cards yet.</span>}
      </div>
    </div>
  );
}

export default function DirectHireDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open && !opts) api.get("/admin/direct-hire/options").then(({ data }) => setOpts(data)).catch(() => setErr("Couldn't load reps")); }, [open, opts]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/direct-hire", f);
      if (data.ok) { toast.success(`${f.first_name} is hired — welcome email sent with their sign-up link`); setOpen(false); setF(EMPTY); }
      else { toast.error(`Saved, but needs retry: ${data.errors.join(" ")}`, { duration: 10000 }); setOpen(false); setF(EMPTY); }
      onDone?.();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Couldn't hire"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="h-7 bg-sam-navy text-xs" data-testid="direct-hire-btn"><UserPlus className="h-3.5 w-3.5 mr-1" />Direct Hire</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="direct-hire-dialog">
        <DialogHeader><DialogTitle>Direct Hire</DialogTitle></DialogHeader>
        <p className="text-xs text-sam-slate">Skip the interview for someone you already know. They get the same welcome email and sign-up link as an approved candidate.</p>
        {!opts ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">First name *</Label><Input value={f.first_name} onChange={set("first_name")} data-testid="dh-first" /></div>
              <div><Label className="text-xs">Last name *</Label><Input value={f.last_name} onChange={set("last_name")} data-testid="dh-last" /></div>
            </div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="dh-email" /></div>
            <div><Label className="text-xs">Mobile phone (for rep texts)</Label><Input value={f.phone} onChange={set("phone")} data-testid="dh-phone" /></div>
            <div><Label className="text-xs">Rep tier</Label>
              <Select value={f.tier} onValueChange={(v) => setF({ ...f, tier: v })}>
                <SelectTrigger data-testid="dh-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{opts.tiers.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <UplinePicker reps={opts.reps} value={f.upline_user_id} onChange={(v) => setF({ ...f, upline_user_id: v })} />
            <ZipPicker zips={opts.zips} value={f.zips} onChange={(v) => setF({ ...f, zips: v })} />
            <div><Label className="text-xs">Internal note (admins only)</Label><Textarea rows={2} value={f.internal_note} onChange={set("internal_note")} placeholder="e.g. met at Chamber, known contact" data-testid="dh-note" /></div>
            {err && <p className="rounded-md bg-sam-crimson/10 p-2 text-sm font-semibold text-sam-crimson" data-testid="dh-error">{err}</p>}
            <Button type="submit" disabled={busy || !f.first_name || !f.last_name || !f.email} className="w-full bg-sam-crimson" data-testid="dh-submit">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Hire and send welcome email</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DirectHireInfo({ a, onRetry }) {
  const dh = a.direct_hire || {};
  return (
    <div className="mt-1.5 rounded-md bg-slate-50 p-2 text-xs text-sam-slate" data-testid={`careers-dh-info-${a.id}`}>
      <b className="text-sam-navy">Direct Hire – no interview</b> · by {dh.by_name || dh.by} on {String(dh.at || "").slice(0, 10)} · upline {dh.upline_name || "Saving America Money"} · {dh.tier === "lead" ? "Lead rep" : "Basic rep"}{dh.zips?.length ? ` · ZIPs ${dh.zips.join(", ")}` : ""}
      {dh.internal_note && <div className="mt-0.5">Note: {dh.internal_note}</div>}
      {a.status === "needs_retry" && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="font-semibold text-sam-crimson" data-testid={`careers-dh-error-${a.id}`}>{a.last_error}</span>
          <Button size="sm" className="h-6 bg-sam-crimson text-xs" onClick={onRetry} data-testid={`careers-dh-retry-${a.id}`}>Retry</Button>
        </div>
      )}
    </div>
  );
}
